import { TFunction } from "i18next";
import * as Yup from "yup";
import { DelegationType } from "../context/store/reducers/fleetManagerContext.reducer";
import { format } from "../utils";
import useIsValidDelegeeEmail from "./useIsValidDelegeeEmail";

export interface IRightSelection {
  pmcId: number;
  contractType: number;
}

export interface IDelegeeFormValues {
  name?: string;
  email: string;
  rights?: IRightSelection[];
  productDates?: Record<string, { activationDate: Date | null; expirationDate: Date | null }>;
}

const useValidationSchema = (t: TFunction, isEditMode: boolean, delegationType: DelegationType, translationContext: string) => {
  const isDelegeeEmailValid = useIsValidDelegeeEmail();

  const rightObjectSchema = Yup.object({
    pmcId: Yup.number().required(),
    contractType: Yup.number().required(),
  });

  const rights = isEditMode
    ? Yup.array(rightObjectSchema).default([])
    : Yup.array(rightObjectSchema)
        .min(1, format(t("errors:min.field"), t("errors:"+translationContext+":product"), 1))
        .required(format(t("errors:required.field"), t("errors:"+translationContext+":product").toLowerCase()))
        .test("same-contract-type", (value, ctx) => {
          if (delegationType !== DelegationType.Multiple) return true;
          if (!value || value.length <= 1) return true;
          
          const contractTypes = value.map((right) => (right as IRightSelection).contractType);
          const firstContractType = contractTypes[0];
          const allSameType = contractTypes.every(type => type === firstContractType);
          
          if (!allSameType) {
            return ctx.createError({ 
              message: t("errors:mixedContractTypes")
            });
          }
          
          return true;
        })
        .default([]);

  const name = Yup.string()
      .default("")
      .optional()
      .max(200, t("errors:maxLength", { field: t("globals:name"), count: 200 }));

  const email =
    delegationType == DelegationType.Multiple
      ? Yup.string().default("")
      : Yup.string()
          .email(
            format(t("errors:format.field"), t("globals:email"), t("globals:email").toLowerCase())
          )
          .required(format(t("errors:required.field"), t("globals:email").toLowerCase()))
          .test("email-is-valid", (value, ctx) => {
            if (!value) return true;

            const validValue = isDelegeeEmailValid(value);
            if (!validValue.isValid)
              return ctx.createError({ message: t(validValue.errorMessageKey!) });

            return true;
          })
          .default("");

  const validationSchema = Yup.object({
    name,
    email,
    rights,
  });

  const initialValues: IDelegeeFormValues = {
    name: "",
    email: "",
    rights: [],
    productDates: {},
  };

  return {
    validationSchema,
    initialValues,
  };
};

export default useValidationSchema;
